import {useEffect, useState, useRef} from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import {CATALOG_STORE_KEY} from "../../constants";
import {useDispatch} from "@wordpress/data";

export function HeaderSearchPopup({inputElement}) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [menuRect, setMenuRect] = useState(null);
    const containerRef = useRef(null);
    const dispatch = useDispatch(CATALOG_STORE_KEY);

    useEffect(() => {
        if (!inputElement) return;

        const handleInput = (e) => {
            setQuery(e.target.value);
            if (e.target.value.length > 0) {
                setIsOpen(true);
            } else {
                setIsOpen(false);
            }
        };

        const handleFocus = () => {
            if (inputElement.value.length > 0) {
                setIsOpen(true);
            }
            recomputeRect();
        };

        const recomputeRect = () => {
            const rect = inputElement.getBoundingClientRect();
            const headerInner = document.querySelector('.pt-header__inner');
            const headerRect = headerInner ? headerInner.getBoundingClientRect() : rect;
            const isMobile = window.innerWidth < 768;

            setMenuRect({
                top: rect.bottom, // ← no window.scrollY
                left: isMobile ? 0 : headerRect.left + window.scrollX,
                width: isMobile ? window.innerWidth : headerRect.width,
            });
        };

        inputElement.addEventListener('input', handleInput);
        inputElement.addEventListener('focus', handleFocus);
        window.addEventListener('resize', recomputeRect);
        window.addEventListener('scroll', recomputeRect);

        return () => {
            inputElement.removeEventListener('input', handleInput);
            inputElement.removeEventListener('focus', handleFocus);
            window.removeEventListener('resize', recomputeRect);
            window.removeEventListener('scroll', recomputeRect);
        };
    }, [inputElement]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target) && !inputElement.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [inputElement]);

    useEffect(() => {
        if (!query || query.length < 2) {
            setResults([]);
            return;
        }

        const timeoutId = setTimeout(async () => {
            setLoading(true);
            try {
                const response = await apiFetch({
                    path: `/api/products?search=${encodeURIComponent(query)}&size=5`,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': window.epiData?.nonce,
                    },
                    body: JSON.stringify([]), // No filters
                });
                setResults(response?.products || []);
            } catch (error) {
                console.error('Search error:', error);
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [query]);

    const handleSearchAll = () => {
        dispatch.stageSearch(query);
        dispatch.commitStagedFilters();
        const params = new URLSearchParams();
        params.set('search', query);
        window.location.assign(`/shop?${params.toString()}`);
        setIsOpen(false);
    };

    if (!isOpen || !menuRect) return null;

    return (
        <div
            ref={containerRef}
            className="fixed z-[999] bg-white shadow-xl rounded-lg mt-1 border border-base-200 overflow-hidden"
            style={{
                top: `${menuRect.top}px`,
                left: `${menuRect.left}px`,
                width: `${menuRect.width}px`,
            }}
        >
            <div className="flex flex-col">
                <button
                    className="flex items-center gap-3 px-4 py-3 hover:bg-base-100 text-left border-b border-base-200 group full-width cursor-pointer"
                    onClick={handleSearchAll}
                >
                    <div className="w-8 h-8 flex items-center justify-center rounded bg-primary/10 text-primary">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                        </svg>
                    </div>
                    <span className="font-medium text-base-content group-hover:text-primary transition-colors">
                        Search for "{query}"
                    </span>
                </button>

                {loading && (
                    <div className="p-4 text-center">
                        <span className="loading loading-spinner loading-md text-primary"></span>
                    </div>
                )}

                {!loading && results.length > 0 && (
                    <div className="">
                        {results.map((product) => (
                            <a
                                key={product.sku}
                                href={`/product/${product.woocommerceSlug}`}
                                className="flex items-center gap-4 px-4 py-3 hover:bg-base-100 transition-colors border-b border-base-100 last:border-0"
                            >
                                <div
                                    className="w-12 h-12 flex-shrink-0 bg-white rounded border border-base-200 overflow-hidden">
                                    <img
                                        src={product.image || product.thumbnailImage}
                                        alt={product.name}
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-base-content truncate m-0">
                                        {product.name}
                                    </h4>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-primary font-bold">
                                            ${product.salePrice?.toFixed(2)}
                                        </span>
                                        {product.regularPrice > product.salePrice && (
                                            <span className="text-xs text-gray-400 line-through">
                                                ${product.regularPrice?.toFixed(2)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                )}

                {!loading && results.length === 0 && query.length >= 2 && (
                    <div className="p-4 text-center text-gray-500 text-sm">
                        No products found
                    </div>
                )}
            </div>
        </div>
    );
}
